import { useContext } from "react";
import { z } from "zod";

import { ChatPromptSubmitContext } from "@/components/ui/chat/ChatPromptSubmitContext";
import type { SuggestionChip } from "@/components/ui/chat/suggestion-chips/SuggestionChips";
import SuggestionChips from "@/components/ui/chat/suggestion-chips/SuggestionChips";
import { heraldPdfExcludeProps } from "@/lib/chat/heraldChatDomDataAttrs";

/** Chat catalog definition for SuggestionChips. Use in renderer catalog. */
export const ChatSuggestionChipsSpec = {
  props: z.object({
    chips: z.array(
      z.object({
        /** Visible button text (short, imperative, 3-6 words). */
        label: z.string(),
        /** Full prompt sent to the chat when clicked. */
        prompt: z.string(),
      }),
    ),
  }),
  description:
    "Row of clickable pill buttons that inject a follow-up prompt into the chat when clicked. " +
    "Use at the END of a response to suggest 2-4 next steps, deeper analysis, or related queries. " +
    "Each chip has a short visible label and a full prompt sent as the user's next message. Limit to 2-4 chips.",
  example: {
    chips: [
      {
        label: "Deep dive this analysis",
        prompt:
          "Deep dive into this analysis — break down the key drivers and show me the underlying data.",
      },
      {
        label: "Compare with competitors",
        prompt:
          "Compare these results against competitor coverage over the same period.",
      },
    ],
  },
} as const;

interface SuggestionChipsPropsFromSpec {
  chips: SuggestionChip[];
}

/**
 * Adapter for json-render defineRegistry: renders SuggestionChips from catalog props.
 * Reads submitPrompt from ChatPromptSubmitContext so clicking a chip sends the
 * prompt as the user's next message.
 */
export const ChatSuggestionChipsFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const context = useContext(ChatPromptSubmitContext);
  const { chips: rawChips } =
    props as unknown as Partial<SuggestionChipsPropsFromSpec>;
  const chips = Array.isArray(rawChips) ? rawChips : [];

  if (!chips || chips.length === 0) {
    return null;
  }

  return (
    <div className="contents" {...heraldPdfExcludeProps}>
      <SuggestionChips
        chips={chips}
        onSubmit={(prompt) => context?.submitPrompt?.(prompt)}
      />
    </div>
  );
};
